import React, { memo, Fragment, useMemo } from 'react'
import { NextPage } from 'next'
import {
  Stack,
  Heading,
  Text,
  Box,
  Flex,
  LinkButton,
  Container,
  Card,
  Price,
  Button,
} from '@thirstycamel/ui'

import LayoutMain from '../layout/LayoutMain'
import CartLine from '../components/CartLine'
import CartLineProduct from '../components/CartLineProduct'
import { useStore, useActions } from '../store/hooks'
import { useMutation, useQuery } from 'react-query'
import TitleBanner from '../components/TitleBanner'
import { backend } from '../utils/backend'
import CartLineBundle from '../components/CartLineBundle'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import doesCartHaveIncompleteBundle from '../utils/doesCartHaveIncompleteBundle'
import useMissedSavings from '../hooks/useMissedSavings'
import { useUser } from '../hooks/useUser'
import MissedSavingsBanner from '../components/MissedSavingsBanner/MissedSavingsBanner'

const Cart: NextPage = () => {
  const { cart, isFetching } = useStore(store => store.cart)
  const fetchCart = useActions(actions => actions.cart.fetchCart)
  const { isLoggedIn } = useUser()
  const missedSavings = useMissedSavings()

  const [emptyCart, emptyState] = useMutation(async () => {
    await backend('cart/empty', { method: 'patch', data: { cart: cart.id } })
    await fetchCart()
  })

  const isLoading = emptyState.status === 'loading' || isFetching

  const hasIncompleteBundle = useMemo(() => doesCartHaveIncompleteBundle(cart), [cart])

  const handleEmptyCart = async () => {
    emptyCart().finally(() => {
      fetchCart()
    })
  }

  return (
    <LayoutMain title="Cart" noContainer>
      <TitleBanner title="My Cart" textAlign="center" pt={[6, 12]} pb={[6, 12]} as="h3" />

      {!cart?.cartItems?.length ? (
        <Stack py={[6, 8]} textAlign="center" align="center" spacing={5}>
          <Text>Your cart is empty.</Text>

          <LinkButton href="/" variantColor="pink">
            Return to store
          </LinkButton>
        </Stack>
      ) : (
        <Box py={[4, 8]}>
          <Container size="md" px={[3, 5, 6, 8]}>
            <Stack spacing={6}>
              <Flex align="flex-start" direction={['column', , 'row']}>
                <Card
                  flex={[, , '2 1 0']}
                  mr={[, , 6, 10]}
                  mb={[4, , 0]}
                  bg="white"
                  border="2px"
                  borderColor="gray.200"
                  w="100%"
                >
                  <Stack
                    isInline
                    spacing={4}
                    py={5}
                    px={[4, 8]}
                    align="center"
                    bg="green.50"
                    borderBottom="2px"
                    borderColor="gray.200"
                  >
                    <Text fontWeight="medium" color="blackAlpha.700" textAlign={['center', 'left']}>
                      From &nbsp;
                      <Text color="pink.500" fontWeight="bold" as="span">
                        {cart?.store?.name}
                      </Text>
                    </Text>
                  </Stack>

                  <Stack shouldWrapChildren px={5}>
                    {cart?.cartItems.map((cartItem, index) => {
                      if (cartItem.parent) return null

                      return (
                        <Fragment key={cartItem.id}>
                          {index > 0 && <Box height="2px" w="100%" bg="gray.200" />}

                          {cartItem.product?.type === ProductType.BUNDLE ? (
                            <CartLineBundle line={cartItem} my={4} />
                          ) : (
                            <CartLineProduct line={cartItem} my={4} />
                          )}
                        </Fragment>
                      )
                    })}
                  </Stack>
                </Card>

                <Stack
                  as={Card}
                  flex={[, , '1 0 0']}
                  spacing={8}
                  py={[5, 6, , 8]}
                  px={[5, 6, , 8]}
                  w="100%"
                >
                  <Stack spacing={4}>
                    <CartLine label="Subtotal" price={cart?.subtotal} />

                    {cart?.promotionsTotal > 0 && (
                      <CartLine label="Promotions" price={-cart?.promotionsTotal} />
                    )}

                    {cart?.humpClubDiscountTotal > 0 && (
                      <CartLine
                        label="Hump Club Offers"
                        subLabel={cart?.applicableHumpClubOffers
                          ?.map(offer => offer.name)
                          .join(', ')}
                        price={-cart?.humpClubDiscountTotal}
                      />
                    )}

                    <CartLine
                      label={cart?.type === 'TAH' ? 'Delivery' : 'Click & Collect'}
                      price={cart?.shippingTotal}
                      isFree={cart?.shippingTotal === 0}
                    />

                    {cart?.shippingPromotionsTotal > 0 && (
                      <CartLine
                        label="Delivery Promotions"
                        price={-cart?.shippingPromotionsTotal}
                      />
                    )}

                    <Flex direction="row" justify="space-between" align="baseline">
                      <Heading>TOTAL</Heading>

                      <Stack textAlign="right" spacing={1}>
                        <Price
                          value={cart?.total || 0}
                          fontFamily="heading"
                          fontSize="2xl"
                          fontWeight="bold"
                        />

                        <Text color="gray.500" fontSize="xs">
                          (includes <Price value={cart?.gstTotal || 0} display="inline" /> GST)
                        </Text>
                      </Stack>
                    </Flex>
                    {!isLoggedIn && missedSavings.hasPotentialSavings && (
                      <MissedSavingsBanner
                        totalDiscountAmount={missedSavings.totalDiscountAmount}
                        signInVariant="modal"
                        variant="small"
                      />
                    )}
                  </Stack>

                  <Stack spacing={5}>
                    {hasIncompleteBundle && (
                      <Text textAlign="center" color="blackAlpha.600" fontSize="sm">
                        Please ensure there are no incomplete bundles in your cart before checking
                        out.
                      </Text>
                    )}

                    <LinkButton
                      href="/checkout"
                      size="lg"
                      variantColor="pink"
                      isLoading={isLoading}
                      isDisabled={!cart?.cartItems.length || hasIncompleteBundle}
                    >
                      Checkout
                    </LinkButton>

                    <Button
                      variantColor="pink"
                      type="button"
                      variant="ghost"
                      isDisabled={!cart.cartItems?.length || isLoading}
                      onClick={() => handleEmptyCart()}
                    >
                      Empty Cart
                    </Button>
                  </Stack>
                </Stack>
              </Flex>
            </Stack>
          </Container>
        </Box>
      )}
    </LayoutMain>
  )
}

export default memo(Cart)
